import { useEffect, useRef } from 'react';
import { useAppSelector } from 'app/hooks';
import useUnlockInsuranceFlow from 'modules/unlock-insurance/hooks/useUnlockInsuranceFlow';
import { selectIsUnlockInsuranceSurfaceOpen } from 'modules/unlock-insurance/services/unlockInsuranceSelectors';

type Props = {
  isOpen: boolean;
  handleNextModalSequence: () => void;
};

/* Trigger-only, unlike its sibling queue entries: the surfaces themselves render from the
   module's single mount point in WithSidebar, so this renders null and only advances the queue. */
function UnlockInsuranceTrigger({ isOpen, handleNextModalSequence }: Props) {
  const { triggerOnLoad } = useUnlockInsuranceFlow();
  const isSurfaceOpen = useAppSelector(selectIsUnlockInsuranceSurfaceOpen);

  const hasRunRef = useRef(false);
  const wasOpenRef = useRef(false);
  const hasAdvancedRef = useRef(false);

  /* The flow latches the session flag itself; but when it ends without opening a surface
     nothing else advances the queue, and the sequence would stall here. */
  useEffect(() => {
    if (!isOpen || hasRunRef.current) return undefined;
    hasRunRef.current = true;

    let cancelled = false;
    triggerOnLoad().then((surfaceOpened) => {
      if (!surfaceOpened && !cancelled) handleNextModalSequence();
    });

    return () => {
      cancelled = true;
    };
  }, [isOpen, triggerOnLoad, handleNextModalSequence]);

  /* Advance the queue once the surface this entry opened closes — `handleNextModalSequence`
     is a fresh identity on every parent render, hence the latch. */
  useEffect(() => {
    if (isSurfaceOpen) {
      wasOpenRef.current = true;
      return;
    }
    if (!wasOpenRef.current || hasAdvancedRef.current) return;
    hasAdvancedRef.current = true;
    handleNextModalSequence();
  }, [isSurfaceOpen, handleNextModalSequence]);

  return null;
}

export default UnlockInsuranceTrigger;
